import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { X, Loader2, Save, Building2 } from 'lucide-react';

/* ========== EDIT USER MODAL ========== */
export const EditUserModal = ({ userData, onClose, onSave, onAssignPlan, loading, currentUserRole, enterprisePlans = [] }) => {
  const [name, setName] = useState(userData.name || '');
  const [tier, setTier] = useState(userData.subscription_tier || 'free');
  const [role, setRole] = useState(userData.role || 'user');
  const [creditsBonus, setCreditsBonus] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState(userData.enterprise_plan_id || '');

  const activePlans = enterprisePlans.filter(p => p.active);
  const currentPlan = enterprisePlans.find(p => p.plan_id === userData.enterprise_plan_id);

  const handleSave = () => {
    const updates = {};
    if (name !== (userData.name || '')) updates.name = name;
    if (tier !== (userData.subscription_tier || 'free')) updates.subscription_tier = tier;
    if (role !== (userData.role || 'user')) updates.role = role;
    if (creditsBonus && !isNaN(creditsBonus) && parseInt(creditsBonus) > 0) {
      updates.credits_bonus = parseInt(creditsBonus);
    }
    // Handle enterprise plan changes via assignment
    if (tier === 'enterprise' && selectedPlanId !== (userData.enterprise_plan_id || '')) {
      updates.enterprise_plan_id = selectedPlanId || '';
      if (selectedPlanId) {
        const plan = enterprisePlans.find(p => p.plan_id === selectedPlanId);
        if (plan) updates.custom_credit_limit = plan.credit_limit;
      } else {
        updates.custom_credit_limit = null;
      }
    }
    // If downgrading from enterprise, clear plan
    if (tier !== 'enterprise' && userData.subscription_tier === 'enterprise') {
      updates.enterprise_plan_id = '';
      updates.custom_credit_limit = null;
    }
    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }
    onSave(userData.user_id, updates);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="edit-user-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#DCEBFF] flex items-center justify-center">
              <span className="text-[#0B3D91] font-semibold text-sm">
                {(userData.name || userData.email || '?').charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="font-outfit font-semibold text-slate-900">Edit User</h3>
              <p className="text-xs text-slate-500">{userData.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="User name"
              data-testid="edit-user-name"
            />
          </div>

          {/* Tier */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subscription Tier</label>
            <select
              value={tier}
              onChange={(e) => {
                setTier(e.target.value);
                if (e.target.value !== 'enterprise') setSelectedPlanId('');
              }}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B3D91]/20"
              data-testid="edit-user-tier"
            >
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          {/* Enterprise Plan Selection */}
          {tier === 'enterprise' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-purple-600" />
                <label className="text-sm font-medium text-purple-700">Enterprise Plan</label>
              </div>
              {activePlans.length === 0 ? (
                <p className="text-xs text-purple-500">No active enterprise plans. Create one in the Enterprise Plans tab first.</p>
              ) : (
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="w-full border border-purple-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-300"
                  data-testid="edit-user-enterprise-plan"
                >
                  <option value="">No plan (unlimited credits)</option>
                  {activePlans.map(plan => (
                    <option key={plan.plan_id} value={plan.plan_id}>
                      {plan.name} — ${plan.price_monthly?.toLocaleString()}/mo ({plan.credit_limit?.toLocaleString()} credits)
                    </option>
                  ))}
                </select>
              )}
              {currentPlan && (
                <p className="text-xs text-purple-500">
                  Current: {currentPlan.name} ({currentPlan.credit_limit?.toLocaleString()} credits/mo)
                </p>
              )}
              {userData.custom_credit_limit && (
                <p className="text-xs text-purple-500">
                  Active credit limit: {userData.custom_credit_limit?.toLocaleString()} credits/month
                </p>
              )}
            </div>
          )}

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B3D91]/20"
              data-testid="edit-user-role"
              disabled={userData.role === 'owner'}
            >
              <option value="user">User</option>
              <option value="manager">Manager</option>
              {['admin', 'owner'].includes(currentUserRole) && <option value="admin">Admin</option>}
              {(currentUserRole === 'owner' || userData.role === 'owner') && <option value="owner">Owner</option>}
            </select>
            {userData.role === 'owner' && (
              <p className="text-xs text-purple-600 mt-1">Owner role can only be changed via Transfer Ownership</p>
            )}
          </div>

          {/* Bonus Credits */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Grant Bonus Credits</label>
            <Input
              type="number"
              min="0"
              value={creditsBonus}
              onChange={(e) => setCreditsBonus(e.target.value)}
              placeholder="0"
              data-testid="edit-user-credits"
            />
            <p className="text-xs text-slate-400 mt-1">Extra credits added to this billing cycle</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50/50">
          <Button variant="outline" onClick={onClose} data-testid="edit-user-cancel">Cancel</Button>
          <Button onClick={handleSave} className="bg-[#0B3D91]" disabled={loading} data-testid="edit-user-save">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};
